import React, { useMemo, useState } from "react";
import { BrowserRouter, useRoutes, Navigate } from "react-router-dom";
import { publicRoutes, privateRoutes } from "./routes";
import ServiceWrapper from "../components/ServiceWrapper";
import AuthBootstrapper from "./AuthBootstrapper";
import { getOrganizationFromToken } from "../utils/token";

// publicRoutes 배열을 useRoutes용 라우트 객체로 변환
function buildPublicRouteObjects() {
  return publicRoutes.map(({ path, component: Component, ...otherProps }) => ({
    path,
    // 필요 시 otherProps를 그대로 컴포넌트에 전달
    element: <Component {...otherProps} />,
  }));
}

// organizations(단일 문자열)에서 MEGAINFO 여부만 정확 일치로 판정
function hasMegaInfoOrganization() {
  const org = getOrganizationFromToken() || sessionStorage.getItem("organization");
  return org === "MEGAINFO";
}

// privateRoutes 배열(children/sub 구조 포함)을 평탄화하여 라우트 객체로 변환
function buildPrivateRouteObjects() {
  const routeObjects = [];

  const restrictByBranchVisible = !hasMegaInfoOrganization();

  // MEGAINFO가 없으면 isBranchVisible === true 인 항목만 유지
  const filteredPrivateRoutes = restrictByBranchVisible
    ? privateRoutes.filter((r) => r.isBranchVisible === true)
    : privateRoutes;

  filteredPrivateRoutes.forEach((route) => {
    const { path: basePath, component: BaseComponent, children, redirect } = route;

    // redirect가 정의된 루트 경로 처리
    if (redirect) {
      routeObjects.push({
        path: basePath,
        element: (
          <Navigate
            to={redirect}
            replace
          />
        ),
      });
    } else if (BaseComponent && !children) {
      // children이 없고 단일 컴포넌트만 있는 경우 ServiceWrapper로 감싸서 렌더
      routeObjects.push({
        path: basePath,
        element: (
          <ServiceWrapper>
            <BaseComponent />
          </ServiceWrapper>
        ),
      });
    }

    // children이 존재하는 경우: child/sub를 순회하며 완전한 경로로 평탄화
    if (children) {
      // MEGAINFO 없으면 children에서도 isBranchVisible === true만 노출
      const filteredChildren = restrictByBranchVisible
        ? children.filter((c) => c.isBranchVisible === true)
        : children;

      filteredChildren.forEach((child) => {
        const { path: childPath, component: ChildComponent, sub } = child;

        // 2단계 하위(sub) 라우트가 있는 경우
        if (sub && Array.isArray(sub)) {
          sub.forEach((subChild) => {
            const SubComponent = subChild.component;
            routeObjects.push({
              path: `${basePath}${childPath}${subChild.path}`,
              element: (
                <ServiceWrapper>
                  {/* 상위 BaseComponent가 있으면 함께 렌더 */}
                  {BaseComponent ? <BaseComponent /> : null}
                  <SubComponent />
                </ServiceWrapper>
              ),
            });
          });
        } else if (ChildComponent) {
          // 일반 children 레벨 라우트
          routeObjects.push({
            path: `${basePath}${childPath}`,
            element: (
              <ServiceWrapper>
                <ChildComponent />
              </ServiceWrapper>
            ),
          });
        }
      });
    }
  });

  return routeObjects;
}

// useRoutes로 라우트 객체를 React 엘리먼트 트리로 변환
function AppRoutes() {
  // 로그인 후 토큰/organization이 세팅되면 라우트가 재생성되어
  // MEGAINFO 전용(=isBranchVisible 없는) 라우트도 즉시 매칭되도록 한다.
  // (기존 useMemo([]) 고정 시: 로그인 직후 첫 진입에서 라우트 미매칭으로 백화 가능)
  const organizationKey =
    getOrganizationFromToken() || sessionStorage.getItem("organization") || "";

  const routes = useMemo(() => {
    const publicRouteObjects = buildPublicRouteObjects();
    const privateRouteObjects = buildPrivateRouteObjects();
    // public → private 순으로 결합하여 선언 순서 유지
    return [...publicRouteObjects, ...privateRouteObjects];
  }, [organizationKey]);

  return useRoutes(routes);
}

// 최상위 Router: BrowserRouter 컨텍스트 하에 AppRoutes 렌더
export default function Router() {
  const [bootstrapped, setBootstrapped] = useState(false);

  return (
    <BrowserRouter>
      {!bootstrapped ? (
        <AuthBootstrapper onReady={() => setBootstrapped(true)} />
      ) : (
        <AppRoutes />
      )}
    </BrowserRouter>
  );
}
